import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import i18next from 'i18next';

// Tipos para las respuestas de error
interface ErrorResponse {
  message: string;
  status: number;
  details?: string;
}

// Configuración de rate limiting
const RATE_LIMIT = {
  maxRequests: 50, // Máximo número de peticiones
  timeWindow: 60000, // Ventana de tiempo en milisegundos (1 minuto)
  requests: [] as number[], // Array para almacenar timestamps de peticiones
};

// Variable para controlar el modo debug
let isDebugMode = false;

// Función para activar/desactivar el modo debug
export const toggleDebugMode = (): boolean => {
  isDebugMode = !isDebugMode;
  return isDebugMode;
};

// Función para verificar si el modo debug está activo
export const isDebugModeActive = (): boolean => {
  return isDebugMode;
};

// Función para verificar el rate limit
const checkRateLimit = (): boolean => {
  // Si el modo debug está activo, ignorar el rate limit
  if (isDebugMode) return true;

  const now = Date.now();
  // Eliminar peticiones antiguas
  RATE_LIMIT.requests = RATE_LIMIT.requests.filter(
    timestamp => now - timestamp < RATE_LIMIT.timeWindow
  );

  // Verificar si hemos excedido el límite
  if (RATE_LIMIT.requests.length >= RATE_LIMIT.maxRequests) {
    return false;
  }

  // Añadir la nueva petición
  RATE_LIMIT.requests.push(now);
  return true;
};

// Función para obtener el token de forma segura
const getToken = (): string | null => {
  try {
    const storage = globalThis?.localStorage;
    if (storage) {
      const token = storage.getItem('token');
      if (isDebugMode && token) {
        console.error('🔑 Debug Mode: Token encontrado', token.substring(0, 10) + '...');
      }
      return token;
    }
    return null;
  } catch (error) {
    console.error('Error accessing localStorage:', error);
    return null;
  }
};

// Función para validar el formato del token JWT
const isValidJwtFormat = (token: string): boolean => {
  // Verificación básica de formato JWT (header.payload.signature)
  const parts = token.split('.');
  return parts.length === 3;
};

// Crear instancia de axios con configuración base
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'lh-content-manager-secure-key-2024',
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
  },
});

// Interceptor para las peticiones
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Verificar rate limit
    if (!checkRateLimit()) {
      return Promise.reject(new Error('Rate limit exceeded. Please try again later.'));
    }

    // Obtener el token JWT
    const token = getToken();
    if (token) {
      // Validar formato del token
      if (isValidJwtFormat(token)) {
        config.headers['Authorization'] = `Bearer ${token}`;
      } else if (isDebugMode) {
        config.headers['Authorization'] = token;
      }
    }

    // Añadir headers de seguridad adicionales
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.headers['X-API-Key'] = 'lh-content-manager-secure-key-2024';

    // Si el modo debug está activo, añadir header especial
    if (isDebugMode) {
      config.headers['X-Debug-Mode'] = 'true';
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Interceptor para las respuestas
apiClient.interceptors.response.use(
  response => {
    // Si el modo debug está activo, mostrar información de la respuesta
    if (isDebugMode) {
      console.error('🔍 Debug Mode: Response from', response.config.url, response);
    }
    return response.data;
  },
  (error: AxiosError<ErrorResponse>) => {
    if (error.response) {
      // El servidor respondió con un status code fuera del rango 2xx
      switch (error.response.status) {
        case 401:
          // Manejar error de autenticación
          console.error('Authentication error: Invalid or expired token');
          // Limpiar token y redirigir al login
          localStorage.removeItem('token');
          // Propagar el error con un mensaje específico para la UI
          return Promise.reject({
            message: i18next.t('status.authError'),
            status: 401,
            details: i18next.t('status.authErrorDetails'),
          });
        case 403:
          // Manejar error de autorización
          console.error('Authorization error: Insufficient permissions');
          // Propagar el error con un mensaje específico para la UI
          return Promise.reject({
            message: i18next.t('status.forbiddenError'),
            status: 403,
            details: i18next.t('status.forbiddenErrorDetails'),
          });
        case 404:
          // Manejar error de recurso no encontrado
          console.error('Resource not found');
          return Promise.reject({
            message: i18next.t('status.notFoundError'),
            status: 404,
            details: i18next.t('status.notFoundErrorDetails'),
          });
        case 429:
          // Manejar error de rate limiting
          console.error('Rate limit exceeded. Please try again later.');
          return Promise.reject({
            message: i18next.t('status.rateLimitError'),
            status: 429,
            details: i18next.t('status.rateLimitErrorDetails'),
          });
        case 500:
          // Manejar error del servidor
          console.error('Server error');
          return Promise.reject({
            message: i18next.t('status.serverError'),
            status: 500,
            details: i18next.t('status.serverErrorDetails'),
          });
        default:
          // Para otros errores, propagar el mensaje original
          return Promise.reject({
            message: error.response.data?.message || i18next.t('status.error'),
            status: error.response.status,
            details: error.response.data?.details || i18next.t('status.error'),
          });
      }
    } else if (error.request) {
      // La petición fue hecha pero no se recibió respuesta
      console.error('No response from server');
      return Promise.reject({
        message: i18next.t('status.networkError'),
        status: 503,
        details: i18next.t('status.networkErrorDetails'),
      });
    } else {
      // Algo sucedió en la configuración de la petición que causó un error
      console.error('Request configuration error:', error.message);
      return Promise.reject({
        message: i18next.t('status.error'),
        status: 500,
        details: i18next.t('status.error'),
      });
    }
  }
);

export default apiClient;
